import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, RotateCw, Trash2, EyeOff, Eye, FileText, FileArchive, Sheet, Presentation, Captions, Pencil, Check, Search, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const STATUS = {
  queued: "text-[#E9C46A] bg-[#E9C46A]/10 border-[#E9C46A]/30",
  extracting: "text-[#3A86FF] bg-[#3A86FF]/10 border-[#3A86FF]/30",
  indexing: "text-[#3A86FF] bg-[#3A86FF]/10 border-[#3A86FF]/30",
  processing: "text-[#3A86FF] bg-[#3A86FF]/10 border-[#3A86FF]/30",
  indexed: "text-[#2EC4B6] bg-[#2EC4B6]/10 border-[#2EC4B6]/30",
  failed: "text-[#E63946] bg-[#E63946]/10 border-[#E63946]/30",
};
// plain words, so nobody has to guess what the machine is doing
const LABEL = {
  queued: "queued",
  extracting: "reading",
  indexing: "indexing",
  processing: "working",
  indexed: "in memory",
  failed: "failed",
};
const BUSY = ["queued", "extracting", "indexing", "processing"];

const KINDS = [
  { key: "all", label: "All" },
  { key: "document", label: "Documents", icon: FileText },
  { key: "transcript", label: "Transcripts", icon: Captions },
  { key: "sheet", label: "Sheets", icon: Sheet },
  { key: "slides", label: "Slides", icon: Presentation },
  { key: "archive", label: "Archives", icon: FileArchive },
];
const ICON = { sheet: Sheet, slides: Presentation, archive: FileArchive, document: FileText, transcript: Captions };
const SORTS = {
  newest: { label: "Newest first", fn: (a, b) => new Date(b.created_at) - new Date(a.created_at) },
  name: { label: "Name A–Z", fn: (a, b) => a.name.localeCompare(b.name) },
  type: { label: "Type", fn: (a, b) => (a.kind || "").localeCompare(b.kind || "") || a.name.localeCompare(b.name) },
  largest: { label: "Largest first", fn: (a, b) => b.size - a.size },
  passages: { label: "Most passages", fn: (a, b) => b.chunk_count - a.chunk_count },
};
const ACCEPT = ".pdf,.docx,.txt,.md,.html,.htm,.rtf,.pptx,.xlsx,.xlsm,.csv,.tsv,.vtt,.srt,.zip";
const INLINE_MAX = 20 * 1024 * 1024;
const kb = (n) => (n > 1e9 ? `${(n / 1e9).toFixed(2)} GB` : n > 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);
const mmss = (s) => (s ? `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}` : "");
const tag = (d) => (d.ext || (d.name.includes(".") ? d.name.split(".").pop() : d.kind) || "file").toUpperCase().slice(0, 5);

export default function Corpus() {
  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [kind, setKind] = useState("all");
  const [sort, setSort] = useState("newest");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [chunks, setChunks] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [editing, setEditing] = useState(null); // { id, name }
  const [busy, setBusy] = useState([]); // rows the curator just acted on
  const fileRef = useRef();

  const load = () => api.documents().then(setDocs).catch(() => {});
  useEffect(() => { load(); const id = setInterval(load, 3000); return () => clearInterval(id); }, []);
  useEffect(() => { if (selected) api.chunks(selected.id).then(setChunks); else setChunks([]); }, [selected, docs]);

  const onUpload = async (files) => {
    for (const f of files) {
      setUploading({ name: f.name, pct: f.size > INLINE_MAX ? 0 : null });
      try {
        if (f.size > INLINE_MAX) {
          const r = await api.uploadLarge(f, (pct) => setUploading({ name: f.name, pct }));
          toast.success(`${f.name} uploaded — ${r.files?.length || 1} file(s) queued`);
        } else {
          const d = await api.upload(f);
          toast.success(d.files ? `${f.name} unpacked — ${d.files.length} files queued` : `${f.name} uploaded`);
          if (d.id) setSelected(d);
        }
      } catch (e) { toast.error(`${f.name}: ${errMsg(e)}`); }
      load();
    }
    setUploading(null);
    load();
  };

  const act = async (id, fn, msg) => {
    setBusy((b) => [...b, id]);
    try {
      await fn();
      if (msg) toast(msg);
    } catch (e) { toast.error(errMsg(e)); }
    await load();
    setBusy((b) => b.filter((x) => x !== id));
  };

  const saveName = async () => {
    const { id, name } = editing;
    const clean = name.trim();
    setEditing(null);
    if (!clean || clean === docs.find((d) => d.id === id)?.name) return;
    setDocs((ds) => ds.map((d) => (d.id === id ? { ...d, name: clean } : d)));
    try {
      await api.renameDoc(id, clean);
      toast("Renamed — every passage now carries the new name");
    } catch (e) { toast.error(errMsg(e)); }
    load();
  };

  const toggleChunk = async (c) => {
    await api.toggleChunk(c.id, !c.excluded);
    setChunks((cs) => cs.map((x) => (x.id === c.id ? { ...x, excluded: !x.excluded } : x)));
  };

  const kindCounts = useMemo(() => docs.reduce((a, d) => ({ ...a, [d.kind]: (a[d.kind] || 0) + 1 }), {}), [docs]);
  const statusCounts = useMemo(() => docs.reduce((a, d) => ({ ...a, [d.status]: (a[d.status] || 0) + 1 }), {}), [docs]);
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return docs
      .filter((d) => (kind === "all" || d.kind === kind) && (filter === "all" || d.status === filter) && (!needle || d.name.toLowerCase().includes(needle)))
      .sort(SORTS[sort].fn);
  }, [docs, kind, filter, sort, q]);

  return (
    <div className="space-y-8" data-testid="corpus-management">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Memory</p>
          <h2 className="mt-1 font-display text-3xl font-semibold tracking-tight">What this Lab remembers</h2>
        </div>
        <input ref={fileRef} type="file" multiple accept={ACCEPT} className="hidden" data-testid="corpus-file-input" onChange={(e) => onUpload([...e.target.files])} />
        <button data-testid="corpus-upload-btn" onClick={() => fileRef.current.click()} disabled={uploading} className="inline-flex items-center gap-2 rounded-full bg-[#E07A5F] px-4 py-2 text-sm font-semibold text-[#121417] hover:bg-[#F4A261] disabled:opacity-60"><Upload size={14} /> {uploading ? "Uploading…" : "Upload files"}</button>
      </header>

      <div
        data-testid="corpus-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); onUpload([...e.dataTransfer.files]); }}
        onClick={() => fileRef.current.click()}
        className={`cursor-pointer rounded-2xl border border-dashed p-6 text-center transition-colors ${dragging ? "border-[#E07A5F] bg-[#E07A5F]/10" : "border-white/10 bg-[#1A1D21]"}`}
      >
        <p className="text-sm font-medium">Drop the Lab's documents here</p>
        <p className="mx-auto mt-1 max-w-2xl text-xs leading-relaxed text-[#6E7582]">
          Text documents only — PDF, Word, PowerPoint, Excel, CSV, TXT, MD, HTML — and ZIP folders of them,
          unpacked automatically. Files over 20 MB upload in chunks, up to 200 MB.
          Recordings, video and images are not read: transcription and vision cost more than this Lab's budget allows.
        </p>
        {uploading && (
          <p className="mt-3 font-mono text-[11px] text-[#F4A261]" data-testid="upload-progress">
            {uploading.name}{uploading.pct !== null ? ` · ${uploading.pct}%` : " · uploading…"}
          </p>
        )}
      </div>

      <div className="grid gap-4 xl:grid-cols-5">
        <section className="min-w-0 rounded-2xl border border-white/[0.06] bg-[#1A1D21] xl:col-span-3">
          <div className="space-y-3 border-b border-white/[0.06] p-4">
            <div className="flex flex-wrap gap-2" data-testid="corpus-type-filters">
              {KINDS.filter((k) => k.key === "all" || kindCounts[k.key]).map((k) => (
                <button key={k.key} data-testid={`corpus-type-${k.key}`} onClick={() => setKind(k.key)} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs ${kind === k.key ? "border-[#E07A5F] text-[#F4A261]" : "border-white/10 text-[#A1A8B3] hover:border-white/30"}`}>
                  {k.icon && <k.icon size={12} />} {k.label} <span className="font-mono text-[#6E7582]">{k.key === "all" ? docs.length : kindCounts[k.key] || 0}</span>
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="relative min-w-[180px] flex-1">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E7582]" />
                <input
                  data-testid="corpus-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by file name…"
                  className="w-full rounded-full border border-white/10 bg-white/[0.03] py-1.5 pl-8 pr-3 text-xs text-[#F3EFE6] placeholder:text-[#6E7582] focus:border-[#E07A5F] focus:outline-none"
                />
              </div>
              <select
                data-testid="corpus-sort"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="min-w-0 max-w-full shrink rounded-full border border-white/10 bg-[#1A1D21] px-3 py-1.5 text-xs text-[#A1A8B3] focus:border-[#E07A5F] focus:outline-none"
              >
                {Object.entries(SORTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>

            <div className="flex flex-wrap gap-2">
              {["all", "indexed", "extracting", "queued", "failed"].map((f) => (
                <button key={f} data-testid={`corpus-filter-${f}`} onClick={() => setFilter(f)} className={`rounded-full border px-3 py-1 text-[11px] ${filter === f ? "border-[#E07A5F] text-[#F4A261]" : "border-white/10 text-[#6E7582] hover:border-white/30"}`}>
                  {f === "all" ? "Any state" : LABEL[f]} <span className="font-mono">{f === "all" ? docs.length : statusCounts[f] || 0}</span>
                </button>
              ))}
            </div>
          </div>

          {shown.length === 0 && <p className="p-8 text-center text-sm text-[#6E7582]">{docs.length ? "Nothing matches that." : "No documents yet. Upload the Lab's notes, journals and harvests to begin."}</p>}
          <ul className="divide-y divide-white/[0.06]">
            {shown.map((d) => {
              const working = BUSY.includes(d.status) || busy.includes(d.id);
              const I = ICON[d.kind] || FileText;
              return (
                <li key={d.id} data-testid={`doc-row-${d.id}`} onClick={() => setSelected(d)} className={`cursor-pointer px-4 py-3 transition-colors hover:bg-white/[0.02] sm:flex sm:flex-wrap sm:items-center sm:gap-3 ${selected?.id === d.id ? "bg-[#E07A5F]/5" : ""}`}>
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                  <I size={16} className="mt-0.5 shrink-0 text-[#6E7582]" />
                  <div className="min-w-0 flex-1">
                    {editing?.id === d.id ? (
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <input
                          data-testid={`doc-rename-input-${d.id}`}
                          autoFocus
                          value={editing.name}
                          onChange={(e) => setEditing({ id: d.id, name: e.target.value })}
                          onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditing(null); }}
                          className="min-w-0 flex-1 rounded-lg border border-[#E07A5F]/50 bg-white/[0.04] px-2 py-1 text-sm text-[#F3EFE6] focus:outline-none"
                        />
                        <button data-testid={`doc-rename-save-${d.id}`} onClick={saveName} className="rounded-lg p-1.5 text-[#F4A261] hover:bg-white/[0.05]"><Check size={14} /></button>
                      </div>
                    ) : (
                      <div className="flex min-w-0 items-center gap-2">
                        <p className="truncate text-sm">{d.name}</p>
                        <button
                          title="Rename"
                          data-testid={`doc-rename-${d.id}`}
                          onClick={(e) => { e.stopPropagation(); setEditing({ id: d.id, name: d.name }); }}
                          className="shrink-0 rounded-lg p-1 text-[#6E7582] hover:bg-white/[0.05] hover:text-[#F4A261]"
                        >
                          <Pencil size={12} />
                        </button>
                      </div>
                    )}
                    <p className="mt-0.5 text-xs leading-relaxed text-[#6E7582]">
                      <span data-testid={`doc-type-tag-${d.id}`} className="mr-1.5 rounded border border-white/10 px-1.5 py-[1px] font-mono text-[9px] tracking-wider text-[#A1A8B3]">{tag(d)}</span>
                      {kb(d.size)}{d.duration_sec ? ` · ${mmss(d.duration_sec)}` : ""} · {d.chunk_count} {d.chunk_count === 1 ? "passage" : "passages"}
                      {d.error && <span className="text-[#E63946]"> · {d.error}</span>}
                    </p>
                    {working && (
                      <div className="mt-1.5 h-1 w-full max-w-xs overflow-hidden rounded-full bg-white/[0.07]">
                        <div className="h-full rounded-full bg-[#3A86FF] transition-all" style={{ width: `${Math.max(4, d.progress)}%` }} />
                      </div>
                    )}
                  </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2 pl-7 sm:mt-0 sm:pl-0">
                  <span data-testid="ingestion-status-badge" className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase ${STATUS[d.status]}`}>
                    {working && <Loader2 size={10} className="animate-spin" />}
                    {LABEL[d.status] || d.status}
                  </span>
                  <div className="flex gap-1">
                    <button title="Retry" data-testid={`doc-retry-${d.id}`} disabled={working} onClick={(e) => { e.stopPropagation(); act(d.id, () => api.retry(d.id), "Re-queued"); }} className="rounded-lg p-2 text-[#A1A8B3] hover:bg-white/[0.05] disabled:opacity-40"><RotateCw size={14} /></button>
                    <button title="Remove" data-testid={`doc-delete-${d.id}`} onClick={(e) => { e.stopPropagation(); act(d.id, async () => { await api.deleteDoc(d.id); if (selected?.id === d.id) setSelected(null); }, "Removed from this Lab's memory"); }} className="rounded-lg p-2 text-[#A1A8B3] hover:bg-white/[0.05] hover:text-[#E63946]"><Trash2 size={14} /></button>
                  </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>

        <section className="min-w-0 break-words rounded-2xl border border-white/[0.06] bg-[#1A1D21] p-4 sm:p-5 xl:col-span-2" data-testid="chunk-preview">
          <p className="eyebrow">Passages</p>
          <h3 className="mt-1 truncate font-display text-base font-medium">{selected?.name || "Select a document"}</h3>
          {selected && <p className="mt-1 text-xs text-[#6E7582]">{chunks.filter((c) => !c.excluded).length} active · {chunks.filter((c) => c.excluded).length} excluded. Weak passages are auto-excluded; toggle to override.</p>}
          <ul className="mt-4 max-h-[60vh] space-y-3 overflow-y-auto pr-1">
            {chunks.map((c) => (
              <li key={c.id} className={`rounded-xl border p-3 ${c.excluded ? "border-white/[0.04] opacity-50" : "border-white/[0.08]"}`}>
                <p className="text-sm leading-relaxed text-[#E8E2D4]">{c.text}</p>
                <div className="mt-2 flex items-center justify-between">
                  <span className="font-mono text-[10px] text-[#6E7582]">{c.locator ? `${c.locator} · ` : ""}#{c.idx} · quality {c.quality.toFixed(2)}</span>
                  <button data-testid={`chunk-toggle-${c.id}`} onClick={() => toggleChunk(c)} className="inline-flex items-center gap-1 text-xs text-[#A1A8B3] hover:text-[#F4A261]">
                    {c.excluded ? <><Eye size={12} /> Include</> : <><EyeOff size={12} /> Exclude</>}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
